import { useLocation } from 'react-router-dom';
import type { SavedView } from '@queries/fetchSavedViews';
import { useSavedViews } from '@queries/fetchSavedViews';
import Nav from '@src/routes';
import { useQueryClient } from '@tanstack/react-query';

import { applySavedViewFilters } from '../hooks/useApplySavedViewFilters';
import { useSavedViewsEnabled } from '../hooks/useSavedViewsEnabled';
import { useSavedViewUrlId, useSelectSavedView } from '../hooks/useSavedViewUrlSelection';
import { SavedViewsNavSection } from '../views/SavedViewsNavSection';

export const SavedViewsNavSectionController = ({ isCollapsed }: { isCollapsed?: boolean }) => {
  const enabled = useSavedViewsEnabled();

  const isOnListing = useLocation().pathname === Nav.Submissions;

  const { data: views = [], isRefreshing } = useSavedViews(enabled, isOnListing);
  const activeViewId = useSavedViewUrlId();
  const selectView = useSelectSavedView();
  const queryClient = useQueryClient();

  if (!enabled || views.length === 0) return null;

  const handleSelect = (view: SavedView) => {
    // Re-selecting the active view re-applies its saved filters (resets dirty edits)
    // directly — navigating to the identical URL would neither refire nor add history.
    if (view.id === activeViewId) return applySavedViewFilters(queryClient, view);
    selectView(view.id);
  };

  const rows = views.map((view) => ({
    id: view.id,
    name: view.name,
    count: view.count,
    loading: isRefreshing,
    active: view.id === activeViewId,
    onSelect: () => handleSelect(view),
  }));

  return <SavedViewsNavSection rows={rows} isCollapsed={isCollapsed} />;
};
